import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { LatLng } from 'leaflet';
import { AppContext } from '../../../context/context';
import { useHighlight } from '../../../context/HighlightContext';
import { customMarker, dispatchFixedRouteCaptureLeftClick } from '../functions/map_point_functions';
import type { Layer, Line } from '../../../context/context';
import { indexOsmStopServicesByPointId } from '../../../utils/transitStopService';
import { TransitOsmStopPopupContent } from './TransitStopPopupContent';

const CLUSTER_THRESHOLD = 250;

interface MarkersLayerProps {
    pickMode: 'start' | 'end' | null;
    onPickPoint: (point: any) => boolean;
    onEditLayer: (layer: Layer, layerId: number) => void;
    onContextMenu: (state: { x: number; y: number; point: any }) => void;
    isFixedRouteCaptureMode: boolean;
}

/**
 * Renders all point markers from every visible layer. The whole result is
 * memoized on inputs that actually affect the JSX so unrelated parent
 * re-renders (e.g. map pan -> location-context update) don't rebuild 689
 * marker elements.
 *
 * Callbacks are passed through refs so they can change identity in the
 * parent without invalidating the memo. Highlight state comes from
 * HighlightContext (a separate channel from selectedShapes), so clicking
 * an OSM route line doesn't cascade through here.
 */
const MarkersLayer: React.FC<MarkersLayerProps> = ({
    pickMode,
    onPickPoint,
    onEditLayer,
    onContextMenu,
    isFixedRouteCaptureMode,
}) => {
    const context = useContext(AppContext);
    const { highlightedIds } = useHighlight();

    const onPickPointRef = useRef(onPickPoint);
    const onEditLayerRef = useRef(onEditLayer);
    const onContextMenuRef = useRef(onContextMenu);
    useEffect(() => { onPickPointRef.current = onPickPoint; }, [onPickPoint]);
    useEffect(() => { onEditLayerRef.current = onEditLayer; }, [onEditLayer]);
    useEffect(() => { onContextMenuRef.current = onContextMenu; }, [onContextMenu]);

    const mapLayers = context?.global.mapLayers ?? [];
    const selectedShapes = context?.global.selectedShapes ?? [];
    const selectedSet = useMemo(() => new Set(selectedShapes), [selectedShapes]);

    return useMemo(() => {
        const blocks: React.ReactElement[] = [];

        // Reverse layers so top layer renders on top visually.
        const layers = mapLayers.filter((l: Layer) => !l.hidden).slice().reverse();
        for (const layer of layers) {
            const tripPointIds = new Set<string>();
            for (const shape of layer.shapes) {
                if (
                    shape.type === 'line' &&
                    'originPointId' in shape &&
                    'destPointId' in shape &&
                    (shape as any).originPointId &&
                    (shape as any).destPointId
                ) {
                    tripPointIds.add((shape as any).originPointId);
                    tripPointIds.add((shape as any).destPointId);
                }
            }
            const hasLinkedTripPoints = tripPointIds.size > 0;

            const visiblePoints = layer.shapes
                .slice()
                .reverse()
                .filter((s: any) => s.type === 'point' && !s.hidden);
            if (visiblePoints.length === 0) continue;

            const layerLines = layer.shapes.filter((s): s is Line => s.type === 'line');
            const osmServicesByStopId = indexOsmStopServicesByPointId(layerLines);

            const markers = visiblePoints.map((shape: any) => {
                const isSelected = selectedSet.has(shape.id) || highlightedIds.has(shape.id);
                const osmStopServices = shape.creationSource === 'transit-osm-stop'
                    ? osmServicesByStopId.get(shape.id) ?? []
                    : [];
                return (
                    <Marker
                        key={`point-${shape.id}`}
                        position={new LatLng(shape.coordinates[0], shape.coordinates[1])}
                        icon={customMarker(shape, isSelected, {
                            isTripPointLinked: tripPointIds.has(shape.id),
                            allowColorFallback: !hasLinkedTripPoints,
                        })}
                        draggable={true}
                        eventHandlers={{
                            contextmenu: (e: any) => {
                                e.originalEvent.preventDefault();
                                onContextMenuRef.current({
                                    x: e.originalEvent.pageX,
                                    y: e.originalEvent.pageY,
                                    point: shape,
                                });
                            },
                            click: (e: any) => {
                                if (isFixedRouteCaptureMode) {
                                    e.originalEvent.preventDefault();
                                    e.originalEvent.stopPropagation?.();
                                    dispatchFixedRouteCaptureLeftClick(shape.coordinates[0], shape.coordinates[1]);
                                    return;
                                }
                                if (pickMode) {
                                    onPickPointRef.current(shape);
                                    e.originalEvent.preventDefault();
                                    return;
                                }
                            },
                            dragend: (e: any) => {
                                const { lat, lng } = e.target.getLatLng();
                                const updatedPoint = { ...shape, coordinates: [lat, lng] as [number, number] };
                                const updatedShapes = layer.shapes.map((s: any) => s.id === shape.id ? updatedPoint : s);
                                onEditLayerRef.current({ ...layer, shapes: updatedShapes }, layer.layerId);
                            },
                        }}
                    >
                        {shape.creationSource === 'transit-osm-stop' && (
                            <Popup>
                                <TransitOsmStopPopupContent point={shape} services={osmStopServices} />
                            </Popup>
                        )}
                    </Marker>
                );
            });

            if (visiblePoints.length > CLUSTER_THRESHOLD) {
                blocks.push(
                    <MarkerClusterGroup
                        key={`cluster-${layer.layerId}`}
                        chunkedLoading
                        maxClusterRadius={60}
                    >
                        {markers}
                    </MarkerClusterGroup>
                );
            } else {
                blocks.push(<React.Fragment key={`layer-${layer.layerId}`}>{markers}</React.Fragment>);
            }
        }

        return <>{blocks}</>;
        // pickMode and isFixedRouteCaptureMode are scalars; mapLayers/selectedSet/
        // highlightedIds drive the visual output. Callback refs handle the rest.
    }, [mapLayers, selectedSet, highlightedIds, pickMode, isFixedRouteCaptureMode]);
};

export default MarkersLayer;
